import React from 'react';
import {
  ChevronLeft, FileText, Truck, Package, DollarSign, Receipt, CreditCard,
  ClipboardCheck, Calendar, Weight, Download, Eye, Link2,
  ChevronRight, ImageIcon
} from 'lucide-react';
import { DELIVERY_STATUS_LABELS, DELIVERY_STATUS_COLORS, ORDER_STATUS_LABELS } from '../constants/status';

// 单据类型配置
const DOC_TYPE_CONFIG = {
  arrival_notice: {
    label: '预到货通知单',
    icon: FileText,
    color: 'blue',
    noLabel: '通知单号',
  },
  receipt: {
    label: '收货单',
    icon: ClipboardCheck,
    color: 'green',
    noLabel: '收货单号',
  },
  stockin: {
    label: '入库单',
    icon: Package,
    color: 'purple',
    noLabel: '入库单号',
  },
  settlement: {
    label: '结算单',
    icon: DollarSign,
    color: 'orange',
    noLabel: '结算单号',
  },
  invoice: {
    label: '发票单',
    icon: Receipt,
    color: 'teal',
    noLabel: '发票号',
  },
  payment: {
    label: '付款单',
    icon: CreditCard,
    color: 'green',
    noLabel: '付款单号',
  },
};

// 信息行组件
const InfoRow = ({ label, value, className = '' }) => (
  <div className={`flex justify-between items-center py-2 ${className}`}>
    <span className="text-sm text-slate-500">{label}</span>
    <span className="text-sm font-medium text-slate-800">{value || '-'}</span>
  </div>
);

// 信息卡片组件
const InfoCard = ({ title, icon: Icon, children, className = '' }) => (
  <div className={`bg-white rounded-xl border border-slate-200 overflow-hidden ${className}`}>
    <div className="flex items-center gap-2 px-4 py-3 bg-slate-50 border-b border-slate-200">
      {Icon && <Icon size={16} className="text-slate-500" />}
      <h3 className="font-medium text-slate-700">{title}</h3>
    </div>
    <div className="p-4">{children}</div>
  </div>
);

// 可点击的单据卡片
const ClickableDocCard = ({ docNo, children, onClick, color = 'blue' }) => (
  <div
    onClick={onClick}
    className={`p-3 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100 transition-colors border border-transparent hover:border-${color}-200`}
  >
    <div className="flex items-center justify-between mb-2">
      <span className="text-sm font-medium text-slate-800">{docNo}</span>
      <ChevronRight size={16} className="text-slate-400" />
    </div>
    {children}
  </div>
);

// 送货单状态显示组件
const DeliveryStatusBadge = ({ status }) => {
  const statusConfig = DELIVERY_STATUS_COLORS[status] || { bg: 'bg-gray-100', text: 'text-gray-600', dot: 'bg-gray-500' };
  const statusLabel = DELIVERY_STATUS_LABELS[status] || status;

  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusConfig.bg} ${statusConfig.text}`}>
      {statusLabel}
    </span>
  );
};

// 预到货通知单详情
const ArrivalNoticeDetail = ({ doc, order }) => {
  // 获取送货单状态
  const deliveryStatus = doc.delivery?.status;

  return (
    <div className="space-y-6">
      <InfoCard title="预约信息" icon={Calendar}>
        <div className="grid grid-cols-2 gap-4">
          <InfoRow label="通知单号" value={doc.docNo} />
          <InfoRow label="预约时间" value={doc.reservationTime} />
          <InfoRow label="车牌号" value={doc.vehicleNo} />
          <InfoRow label="司机姓名" value={doc.driverName} />
          <InfoRow label="司机电话" value={doc.driverPhone} />
          <InfoRow label="到货状态" value={
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
              doc.status === '已到货' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'
            }`}>
              {doc.status}
            </span>
          } />
          {deliveryStatus && (
            <InfoRow label="送货单状态" value={<DeliveryStatusBadge status={deliveryStatus} />} />
          )}
        </div>
      </InfoCard>

    <InfoCard title="关联订单" icon={FileText}>
      <div className="grid grid-cols-2 gap-4">
        <InfoRow label="订单编号" value={order?.orderNo} />
        <InfoRow label="订单状态" value={
          <span className="text-slate-800">{ORDER_STATUS_LABELS.workbench[order?.status] || order?.status}</span>
        } />
        <InfoRow label="供应商" value={order?.supplierName} />
        <InfoRow label="物料" value={order?.materialName} />
        <InfoRow label="基地" value={order?.baseName} />
        <InfoRow label="预估重量" value={`${order?.estimatedWeight} 吨`} />
        <InfoRow label="预估金额" value={
          <span className="text-red-600 font-bold">¥{order?.estimatedAmount?.toLocaleString()}</span>
        } />
      </div>
    </InfoCard>
  </div>
  );
};

// 收货单详情 - 增加关联预到货通知单
const ReceiptDetail = ({ doc, order, onNavigateToDocument }) => {
  // 从 delivery 中获取预到货通知单信息
  const arrivalNotice = doc.delivery?.reservation ? {
    docNo: doc.delivery.reservation.reservationNo,
    deliveryId: doc.deliveryId,
    docType: 'arrival_notice',
    reservationTime: doc.delivery.reservation.time,
    vehicleNo: doc.delivery.reservation.vehicleNo,
    driverName: doc.delivery.reservation.driverName,
    status: '已送达',  // 有收货单说明已送达
  } : null;

  // 获取送货单状态
  const deliveryStatus = doc.delivery?.status;

  const handleNavigateToArrivalNotice = () => {
    if (arrivalNotice && onNavigateToDocument) {
      onNavigateToDocument({
        ...arrivalNotice,
        orderId: doc.orderId,
        orderNo: doc.orderNo,
        supplierId: doc.supplierId,
        supplierName: doc.supplierName,
        baseId: doc.baseId,
        baseName: doc.baseName,
        materialName: doc.materialName,
        createdAt: doc.delivery?.reservation?.time,
        delivery: doc.delivery,
      });
    }
  };

  return (
    <div className="space-y-6">
      <InfoCard title="收货信息" icon={ClipboardCheck}>
        <div className="grid grid-cols-2 gap-4">
          <InfoRow label="收货单号" value={doc.docNo} />
          <InfoRow label="到场时间" value={doc.arrivalTime} />
          <InfoRow label="车牌号" value={doc.vehicleNo} />
          <InfoRow label="确认人" value={doc.arrivalOperator} />
          <InfoRow label="入库状态" value={
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
              doc.status === '已入库' ? 'bg-purple-100 text-purple-700' : 'bg-amber-100 text-amber-700'
            }`}>
              {doc.status}
            </span>
          } />
          {deliveryStatus && (
            <InfoRow label="送货单状态" value={<DeliveryStatusBadge status={deliveryStatus} />} />
          )}
        </div>
        {doc.arrivalPhoto && (
          <div className="mt-4 pt-4 border-t border-slate-100">
            <p className="text-xs text-slate-500 mb-2">到场照片</p>
            <div className="w-32 h-24 bg-slate-100 rounded-lg flex items-center justify-center">
              <span className="text-xs text-slate-400">{doc.arrivalPhoto}</span>
            </div>
          </div>
        )}
      </InfoCard>

      {/* 关联的预到货通知单 */}
      {arrivalNotice && (
        <InfoCard title="关联预到货通知单" icon={FileText}>
          <ClickableDocCard
            docNo={arrivalNotice.docNo}
            onClick={handleNavigateToArrivalNotice}
            color="blue"
          >
            <div className="grid grid-cols-3 gap-2 text-xs text-slate-500">
              <span>预约时间: {arrivalNotice.reservationTime}</span>
              <span>车牌: {arrivalNotice.vehicleNo}</span>
              <span>司机: {arrivalNotice.driverName}</span>
            </div>
          </ClickableDocCard>
        </InfoCard>
      )}

      <InfoCard title="关联订单" icon={FileText}>
        <div className="grid grid-cols-2 gap-4">
          <InfoRow label="订单编号" value={order?.orderNo} />
          <InfoRow label="供应商" value={order?.supplierName} />
          <InfoRow label="物料" value={order?.materialName} />
          <InfoRow label="基地" value={order?.baseName} />
          <InfoRow label="预估重量" value={`${order?.estimatedWeight} 吨`} />
        </div>
      </InfoCard>
    </div>
  );
};

// 环节信息组件
const StageInfoCard = ({ title, icon: Icon, stageInfo, defaultOperator }) => {
  if (!stageInfo) return null;

  return (
    <div className="bg-white rounded-lg border border-slate-200 p-4">
      <div className="flex items-center gap-2 mb-3">
        {Icon && <Icon size={16} className="text-slate-500" />}
        <h4 className="font-medium text-slate-700">{title}</h4>
      </div>
      <div className="grid grid-cols-2 gap-3 text-sm">
        <div className="flex justify-between">
          <span className="text-slate-500">操作时间</span>
          <span className="text-slate-800">{stageInfo.at || '-'}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-slate-500">操作员</span>
          <span className="text-slate-800">{stageInfo.operator || defaultOperator || '-'}</span>
        </div>
      </div>
      {stageInfo.photo && (
        <div className="mt-3 pt-3 border-t border-slate-100">
          <p className="text-xs text-slate-500 mb-2">现场照片</p>
          <div className="w-20 h-14 bg-slate-100 rounded-lg flex items-center justify-center overflow-hidden group cursor-pointer hover:bg-slate-200 transition-colors">
            <div className="flex flex-col items-center gap-1">
              <ImageIcon size={18} className="text-slate-400 group-hover:text-slate-500" />
              <span className="text-[10px] text-slate-400 truncate max-w-[70px]">{stageInfo.photo}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// 入库单详情 - 增加关联收货单
const StockinDetail = ({ doc, order, onNavigateToDocument }) => {
  // 从 delivery 中获取收货单信息
  const receipt = doc.delivery?.arrival ? {
    docNo: doc.deliveryNo ? doc.deliveryNo.replace('DEL', 'RC') : `RC-${doc.deliveryId}`,
    deliveryId: doc.deliveryId,
    docType: 'receipt',
    arrivalTime: doc.delivery.arrival.at,
    arrivalOperator: doc.delivery.arrival.operator,
    arrivalPhoto: doc.delivery.arrival.photo,
    vehicleNo: doc.delivery.reservation?.vehicleNo,
    status: doc.delivery.storage ? '已完成' : '已确认',
  } : null;

  // 获取送货单状态
  const deliveryStatus = doc.delivery?.status;

  const handleNavigateToReceipt = () => {
    if (receipt && onNavigateToDocument) {
      onNavigateToDocument({
        ...receipt,
        orderId: doc.orderId,
        orderNo: doc.orderNo,
        supplierId: doc.supplierId,
        supplierName: doc.supplierName,
        baseId: doc.baseId,
        baseName: doc.baseName,
        materialName: doc.materialName,
        createdAt: doc.delivery?.arrival?.at,
        delivery: doc.delivery,
      });
    }
  };

  return (
    <div className="space-y-6">
      {/* 基本信息 */}
      <InfoCard title="入库信息" icon={Package}>
        <div className="grid grid-cols-2 gap-4">
          <InfoRow label="入库单号" value={doc.docNo} />
          <InfoRow label="入库时间" value={doc.stockInTime} />
          <InfoRow label="车牌号" value={doc.vehicleNo} />
          <InfoRow label="操作员" value={doc.operator} />
          <InfoRow label="物料" value={doc.materialName} />
          <InfoRow label="结算状态" value={
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
              doc.delivery?.settlement ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-700'
            }`}>
              {doc.delivery?.settlement ? '已发起' : '未发起'}
            </span>
          } />
          {deliveryStatus && (
            <InfoRow label="送货单状态" value={<DeliveryStatusBadge status={deliveryStatus} />} />
          )}
        </div>
      </InfoCard>

      {/* 过磅信息 */}
      <InfoCard title="过磅信息" icon={Weight}>
        <div className="grid grid-cols-4 gap-4 mb-4">
          <div className="bg-purple-50 rounded-lg p-3 text-center">
            <p className="text-xs text-purple-600">毛重</p>
            <p className="text-lg font-bold text-purple-700">{doc.grossWeight} 吨</p>
          </div>
          <div className="bg-purple-50 rounded-lg p-3 text-center">
            <p className="text-xs text-purple-600">皮重</p>
            <p className="text-lg font-bold text-purple-700">{doc.tareWeight} 吨</p>
          </div>
          <div className="bg-purple-50 rounded-lg p-3 text-center">
            <p className="text-xs text-purple-600">收货量(净重)</p>
            <p className="text-lg font-bold text-purple-700">{doc.actualWeight} 吨</p>
          </div>
          <div className="bg-purple-50 rounded-lg p-3 text-center">
            <p className="text-xs text-purple-600">扣杂量</p>
            <p className="text-lg font-bold text-purple-700">{doc.deductionWeight ?? doc.deduction ?? '-'} 吨</p>
          </div>
        </div>
      </InfoCard>

      {/* 金额信息 */}
      <InfoCard title="金额信息" icon={DollarSign}>
        <div className="p-4 bg-green-50 rounded-lg mb-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-green-600">含税金额</p>
              <p className="text-2xl font-bold text-green-700">¥{doc.amountWithTax?.toLocaleString()}</p>
            </div>
            <div className="text-right">
              <p className="text-xs text-green-600">不含税金额</p>
              <p className="text-lg font-bold text-green-700">¥{doc.amountWithoutTax?.toLocaleString()}</p>
            </div>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-4">
          <InfoRow label="单价" value={`¥${doc.price || order?.price}/吨`} />
          <InfoRow label="税率" value={`${doc.taxRate}%`} />
          <InfoRow label="开票类型" value={doc.invoiceType} />
        </div>
      </InfoCard>

      {/* 入库环节 */}
      <InfoCard title="入库环节" icon={Truck}>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <StageInfoCard
            title="入场"
            icon={Truck}
            stageInfo={doc.entryInfo}
            defaultOperator={doc.operator}
          />
          <StageInfoCard
            title="入场称重"
            icon={Weight}
            stageInfo={doc.entryWeighInfo}
            defaultOperator={doc.operator}
          />
          <StageInfoCard
            title="质检"
            icon={FileText}
            stageInfo={doc.qualityInfo}
            defaultOperator={doc.operator}
          />
          <StageInfoCard
            title="出厂称重"
            icon={Weight}
            stageInfo={doc.exitWeighInfo}
            defaultOperator={doc.operator}
          />
          <StageInfoCard
            title="出厂检查"
            icon={ClipboardCheck}
            stageInfo={doc.exitCheckInfo}
            defaultOperator={doc.operator}
          />
        </div>
      </InfoCard>

      {/* 关联的收货单 */}
      {receipt && (
        <InfoCard title="关联收货单" icon={ClipboardCheck}>
          <ClickableDocCard
            docNo={receipt.docNo}
            onClick={handleNavigateToReceipt}
            color="green"
          >
            <div className="grid grid-cols-3 gap-2 text-xs text-slate-500">
              <span>到场时间: {receipt.arrivalTime}</span>
              <span>车牌: {receipt.vehicleNo}</span>
              <span>确认人: {receipt.arrivalOperator}</span>
            </div>
          </ClickableDocCard>
        </InfoCard>
      )}

      <InfoCard title="关联订单" icon={FileText}>
        <div className="grid grid-cols-2 gap-4">
          <InfoRow label="订单编号" value={order?.orderNo} />
          <InfoRow label="订单状态" value={
            <span className="text-slate-800">{ORDER_STATUS_LABELS.workbench[order?.status] || order?.status}</span>
          } />
          <InfoRow label="供应商" value={order?.supplierName} />
          <InfoRow label="物料" value={order?.materialName} />
          <InfoRow label="基地" value={order?.baseName} />
          <InfoRow label="预估重量" value={`${order?.estimatedWeight} 吨`} />
          <InfoRow label="预估金额" value={
            <span className="text-red-600 font-bold">¥{order?.estimatedAmount?.toLocaleString()}</span>
          } />
        </div>
      </InfoCard>
    </div>
  );
};

// 结算单详情 - 支持多个关联入库单，可点击跳转
const SettlementDetail = ({ doc, sourceDocs, deliveries, stockIns, onNavigateToDocument }) => {
  // 判断是否有关联的入库单（不管是合并还是非合并）
  const hasStockins = sourceDocs?.stockins?.length > 0;
  // 判断是否为合并单据（关联的入库单数量 >= 2）
  const isMerged = doc.isMerged && hasStockins;
  // 单个入库单：从 delivery.storage 获取
  const singleStockinFromDelivery = !isMerged && doc.delivery?.storage;
  // 单个入库单：从 sourceDocs 获取（用于合并结算单但只有1个入库单的情况）
  const singleStockinFromSource = !isMerged && hasStockins && sourceDocs.stockins.length === 1 && !doc.delivery?.storage;
  // 多个入库单
  const multipleStockins = !isMerged && hasStockins && sourceDocs.stockins.length > 1;

  // 处理点击入库单跳转
  const handleStockinClick = (stockinDocNo) => {
    if (!onNavigateToDocument) return;

    // 先在 deliveries 中查找对应的 delivery
    const delivery = deliveries?.find(d => d.storage?.stockInNo === stockinDocNo);

    if (delivery) {
      // 从 delivery 构建入库单文档对象
      const stockinDoc = {
        id: delivery.storage.stockInNo,
        docNo: delivery.storage.stockInNo,
        docType: 'stockin',
        orderId: delivery.orderId,
        orderNo: delivery.orderNo,
        deliveryId: delivery.id,
        deliveryNo: delivery.deliveryNo,
        supplierId: delivery.supplierId,
        supplierName: delivery.supplierName,
        baseId: delivery.baseId,
        baseName: delivery.baseName,
        materialName: delivery.materialName,
        vehicleNo: delivery.reservation?.vehicleNo,
        grossWeight: delivery.storage.grossWeight,
        tareWeight: delivery.storage.tareWeight,
        netWeight: delivery.storage.netWeight,
        deduction: delivery.storage.deduction,
        actualWeight: delivery.storage.actualWeight,
        quality: delivery.storage.quality,
        stockInTime: delivery.storage.at,
        operator: delivery.storage.operator,
        status: delivery.storage.confirmed ? '已确认' : '待确认',
        createdAt: delivery.storage.at,
        // 环节信息（包含照片）
        entryInfo: delivery.storage.entryInfo,
        entryWeighInfo: delivery.storage.entryWeighInfo,
        qualityInfo: delivery.storage.qualityInfo,
        exitWeighInfo: delivery.storage.exitWeighInfo,
        exitCheckInfo: delivery.storage.exitCheckInfo,
        photos: delivery.storage.photos,
        // 金额信息
        price: delivery.storage.price,
        taxRate: delivery.storage.taxRate,
        invoiceType: delivery.storage.invoiceType,
        amountWithTax: delivery.storage.amountWithTax,
        amountWithoutTax: delivery.storage.amountWithoutTax,
        delivery: delivery,
      };
      onNavigateToDocument(stockinDoc);
    } else {
      // 在 stockIns 数组中查找
      const stockIn = stockIns?.find(s => s.id === stockinDocNo);
      if (stockIn) {
        // 从 stockIn 构建入库单文档对象，添加模拟的环节信息
        const baseTime = stockIn.inboundTime || new Date().toISOString().slice(0, 16).replace('T', ' ');
        const stockinDoc = {
          id: stockIn.id,
          docNo: stockIn.id,
          docType: 'stockin',
          supplierId: stockIn.supplierId,
          supplierName: stockIn.supplier,
          baseId: stockIn.base,
          baseName: stockIn.baseName,
          materialName: stockIn.materialName,
          vehicleNo: stockIn.plate,
          grossWeight: stockIn.grossWeight,
          tareWeight: stockIn.tareWeight,
          netWeight: stockIn.netWeight,
          deduction: stockIn.deduction,
          actualWeight: stockIn.actualWeight,
          quality: stockIn.quality,
          stockInTime: stockIn.inboundTime,
          operator: stockIn.operator || '系统',
          status: stockIn.status,
          createdAt: stockIn.inboundTime,
          // 环节信息（使用占位符照片）
          entryInfo: {
            at: baseTime,
            operator: '门卫',
            photo: 'https://picsum.photos/seed/entry_' + stockIn.id + '/200/150',
          },
          entryWeighInfo: {
            at: baseTime,
            operator: stockIn.operator || '司磅员',
            photo: 'https://picsum.photos/seed/weigh_in_' + stockIn.id + '/200/150',
          },
          qualityInfo: {
            at: baseTime,
            operator: '质检员',
            photo: 'https://picsum.photos/seed/quality_' + stockIn.id + '/200/150',
          },
          exitWeighInfo: {
            at: baseTime,
            operator: stockIn.operator || '司磅员',
            photo: 'https://picsum.photos/seed/weigh_out_' + stockIn.id + '/200/150',
          },
          exitCheckInfo: {
            at: baseTime,
            operator: '门卫',
            photo: 'https://picsum.photos/seed/exit_' + stockIn.id + '/200/150',
          },
          photos: stockIn.photos || [],
          delivery: null,
        };
        onNavigateToDocument(stockinDoc);
      }
    }
  };

  return (
    <div className="space-y-6">
      <InfoCard title="结算信息" icon={DollarSign}>
        {isMerged && (
          <div className="mb-3 px-3 py-2 bg-blue-50 rounded-lg text-xs text-blue-700 flex items-center gap-2">
            <Link2 size={14} />
            <span>合并结算单 - 由 {sourceDocs.stockins.length} 个入库单合并生成</span>
          </div>
        )}
        <div className="p-4 bg-orange-50 rounded-lg mb-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-orange-600">结算金额</p>
              <p className="text-2xl font-bold text-orange-700">¥{doc.amount?.toLocaleString()}</p>
            </div>
            <div className="text-right">
              <p className="text-xs text-orange-600">确认状态</p>
              <p className={`text-lg font-bold ${doc.confirmedBySupplier ? 'text-green-600' : 'text-orange-600'}`}>
                {doc.confirmedBySupplier ? '已确认' : '待确认'}
              </p>
            </div>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <InfoRow label="结算单号" value={doc.docNo} />
          <InfoRow label="实重" value={`${doc.actualWeight} 吨`} />
          <InfoRow label="单价" value={`¥${doc.unitPrice}/吨`} />
          <InfoRow label="供应商确认时间" value={doc.confirmedAt || '待确认'} />
        </div>
      </InfoCard>

      {/* 合并单据或多个关联入库单 - 可点击 */}
      {(isMerged || multipleStockins) && (
        <InfoCard title={`关联入库单 (${sourceDocs.stockins.length})`} icon={Package}>
          {isMerged && (
            <div className="mb-3 px-3 py-2 bg-blue-50 rounded-lg text-xs text-blue-700 flex items-center gap-2">
              <Link2 size={14} />
              <span>合并结算单 - 由 {sourceDocs.stockins.length} 个入库单合并生成</span>
            </div>
          )}
          <div className="space-y-3">
            {sourceDocs.stockins.map((stockin) => (
              <ClickableDocCard
                key={stockin.docNo}
                docNo={stockin.docNo}
                onClick={() => handleStockinClick(stockin.docNo)}
                color="purple"
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs text-slate-500">实际重量</span>
                  <span className="text-sm font-bold text-purple-600">{stockin.actualWeight} 吨</span>
                </div>
                <div className="grid grid-cols-3 gap-2 text-xs text-slate-500">
                  <span>车牌: {stockin.vehicleNo || '-'}</span>
                  <span>时间: {stockin.at}</span>
                  <span>操作员: {stockin.operator || '-'}</span>
                </div>
              </ClickableDocCard>
            ))}
            <div className="pt-2 border-t border-slate-200 flex justify-between text-sm">
              <span className="text-slate-500">合计重量</span>
              <span className="font-bold text-purple-600">
                {sourceDocs.stockins.reduce((sum, s) => sum + (s.actualWeight || 0), 0).toFixed(1)} 吨
              </span>
            </div>
          </div>
        </InfoCard>
      )}

      {/* 单个入库单 - 从 delivery.storage 获取 */}
      {singleStockinFromDelivery && (
        <InfoCard title="关联入库单" icon={Package}>
          <ClickableDocCard
            docNo={doc.delivery.storage.stockInNo}
            onClick={() => handleStockinClick(doc.delivery.storage.stockInNo)}
            color="purple"
          >
            <div className="space-y-2">
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div><span className="text-slate-500">入库时间:</span> <span className="text-slate-700">{doc.delivery.storage.at}</span></div>
                <div><span className="text-slate-500">车牌号:</span> <span className="text-slate-700">{doc.delivery.reservation?.vehicleNo}</span></div>
                <div><span className="text-slate-500">操作员:</span> <span className="text-slate-700">{doc.delivery.storage.operator}</span></div>
                <div><span className="text-slate-500">质检等级:</span> <span className="text-purple-600 font-medium">{doc.delivery.storage.quality}</span></div>
              </div>
              <div className="pt-2 border-t border-slate-200 flex justify-between">
                <span className="text-xs text-slate-500">实际重量</span>
                <span className="text-sm font-bold text-purple-600">{doc.delivery.storage.actualWeight} 吨</span>
              </div>
            </div>
          </ClickableDocCard>
        </InfoCard>
      )}

      {/* 单个入库单 - 从 sourceDocs 获取（合并结算单但只有1个入库单） */}
      {singleStockinFromSource && (
        <InfoCard title="关联入库单" icon={Package}>
          {sourceDocs.stockins.map((stockin) => (
            <ClickableDocCard
              key={stockin.docNo}
              docNo={stockin.docNo}
              onClick={() => handleStockinClick(stockin.docNo)}
              color="purple"
            >
              <div className="space-y-2">
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div><span className="text-slate-500">入库时间:</span> <span className="text-slate-700">{stockin.at}</span></div>
                  <div><span className="text-slate-500">车牌号:</span> <span className="text-slate-700">{stockin.vehicleNo || '-'}</span></div>
                  <div><span className="text-slate-500">操作员:</span> <span className="text-slate-700">{stockin.operator || '-'}</span></div>
                  <div><span className="text-slate-500">质检等级:</span> <span className="text-purple-600 font-medium">{stockin.quality || '-'}</span></div>
                </div>
                <div className="pt-2 border-t border-slate-200 flex justify-between">
                  <span className="text-xs text-slate-500">实际重量</span>
                  <span className="text-sm font-bold text-purple-600">{stockin.actualWeight} 吨</span>
                </div>
              </div>
            </ClickableDocCard>
          ))}
        </InfoCard>
      )}
    </div>
  );
};

// 发票单详情 - 支持多个关联结算单，可点击跳转
const InvoiceDetail = ({ doc, sourceDocs, deliveries, onNavigateToDocument }) => {
  // 判断是否为合并发票单
  const isMerged = doc.isMerged && (sourceDocs?.settlements?.length > 0 || (doc.settlementNos?.length || 0) > 1);

  // 获取关联的结算单数据
  const getSettlements = () => {
    // 如果有 sourceDocs.settlements（从 findSourceDocuments 获取）
    if (sourceDocs?.settlements?.length > 0) {
      return sourceDocs.settlements;
    }

    // 如果有 settlementNos（从 InvoiceManageView 聚合数据）
    if (doc.settlementNos?.length > 0) {
      return doc.settlementNos.map(settlementNo => {
        // 尝试从 deliveries 中查找对应的结算单详情
        const delivery = deliveries?.find(d => d.settlement?.settlementNo === settlementNo);
        if (delivery?.settlement) {
          return {
            docNo: settlementNo,
            amount: delivery.settlement.amount,
            actualWeight: delivery.settlement.actualWeight || delivery.storage?.actualWeight,
            unitPrice: delivery.settlement.unitPrice,
            confirmedAt: delivery.settlement.confirmedAt,
            confirmedBySupplier: delivery.settlement.confirmedBySupplier,
          };
        }
        // 返回基本信息
        return { docNo: settlementNo, amount: 0 };
      });
    }

    return [];
  };

  const settlements = getSettlements();
  const hasSettlements = settlements.length > 0;

  // 处理点击结算单跳转
  const handleSettlementClick = (settlementDocNo) => {
    if (!deliveries || !onNavigateToDocument) return;

    // 查找对应的 delivery
    const delivery = deliveries.find(d => d.settlement?.settlementNo === settlementDocNo);
    if (!delivery) return;

    // 构建结算单文档对象
    const settlementDoc = {
      id: delivery.settlement.settlementNo,
      docNo: delivery.settlement.settlementNo,
      docType: 'settlement',
      orderId: delivery.orderId,
      orderNo: delivery.orderNo,
      deliveryId: delivery.id,
      deliveryNo: delivery.deliveryNo,
      supplierId: delivery.supplierId,
      supplierName: delivery.supplierName,
      baseId: delivery.baseId,
      baseName: delivery.baseName,
      materialName: delivery.materialName,
      actualWeight: delivery.settlement.actualWeight || delivery.storage?.actualWeight,
      unitPrice: delivery.settlement.unitPrice,
      amount: delivery.settlement.amount,
      confirmedBySupplier: delivery.settlement.confirmedBySupplier,
      confirmedAt: delivery.settlement.confirmedAt,
      status: delivery.settlement.confirmedBySupplier ? '已结算' : '待结算',
      invoiceStatus: delivery.invoice ? '已开票' : '未开票',
      createdAt: delivery.storage?.at || delivery.createdAt,
      delivery: delivery,
      // 判断是否为合并单据：关联的入库单数量 >= 2
      isMerged: (delivery.settlement.sourceStockInNos?.length || 0) >= 2,
      sourceStockInNos: delivery.settlement.sourceStockInNos || null,
    };

    onNavigateToDocument(settlementDoc);
  };

  return (
    <div className="space-y-6">
      {/* 发票信息卡片 */}
      <InfoCard title="发票信息" icon={Receipt}>
        {isMerged && (
          <div className="mb-3 px-3 py-2 bg-blue-50 rounded-lg text-xs text-blue-700 flex items-center gap-2">
            <Link2 size={14} />
            <span>合并发票单 - 由 {settlements.length} 个结算单合并生成</span>
          </div>
        )}
        <div className="p-4 bg-teal-50 rounded-lg mb-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-teal-600">发票金额</p>
              <p className="text-2xl font-bold text-teal-700">¥{doc.amount?.toLocaleString()}</p>
            </div>
            <div className="text-right">
              <p className="text-xs text-teal-600">开票状态</p>
              <p className={`text-lg font-bold ${doc.status === '已付款' || doc.status === '已开票' ? 'text-green-600' : 'text-amber-600'}`}>
                {doc.status}
              </p>
            </div>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <InfoRow label="发票号" value={doc.docNo} />
          <InfoRow label="发票类型" value={doc.invoiceType} />
          <InfoRow label="开票日期" value={doc.invoiceDate} />
          <InfoRow label="供应商" value={doc.supplierName} />
          <InfoRow label="基地" value={doc.baseName} />
        </div>
        {doc.invoiceFile && (
          <div className="mt-4 pt-4 border-t border-slate-100">
            <button className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700">
              <Download size={16} />
              下载发票文件
            </button>
          </div>
        )}
      </InfoCard>

      {/* 关联结算单卡片 */}
      {hasSettlements && (
        <InfoCard
          title={isMerged ? `关联结算单 (${settlements.length})` : '关联结算单'}
          icon={DollarSign}
        >
          <div className="space-y-3">
            {settlements.map((settlement, index) => (
              <ClickableDocCard
                key={settlement.docNo || index}
                docNo={settlement.docNo}
                onClick={() => handleSettlementClick(settlement.docNo)}
                color="orange"
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs text-slate-500">结算金额</span>
                  <span className="text-sm font-bold text-orange-600">
                    ¥{(settlement.amount || 0).toLocaleString()}
                  </span>
                </div>
                <div className="grid grid-cols-3 gap-2 text-xs text-slate-500">
                  <span>实重: {settlement.actualWeight || '-'} 吨</span>
                  <span>单价: ¥{settlement.unitPrice || '-'}/吨</span>
                  <span>确认: {settlement.confirmedAt || (settlement.confirmedBySupplier ? '已确认' : '待确认')}</span>
                </div>
              </ClickableDocCard>
            ))}
            {isMerged && (
              <div className="pt-2 border-t border-slate-200 flex justify-between text-sm">
                <span className="text-slate-500">合计金额</span>
                <span className="font-bold text-orange-600">
                  ¥{settlements.reduce((sum, s) => sum + (s.amount || 0), 0).toLocaleString()}
                </span>
              </div>
            )}
          </div>
        </InfoCard>
      )}
    </div>
  );
};

// 付款单详情 - 支持多个关联结算单和发票单，可点击跳转
const PaymentDetail = ({ doc, order, sourceDocs, deliveries, onNavigateToDocument }) => {
  const isMerged = doc.isMerged && (sourceDocs?.invoices?.length > 0 || sourceDocs?.settlements?.length > 0);

  // 获取关联的发票单数据
  const getInvoices = () => {
    // 优先从 sourceDocs 获取
    if (sourceDocs?.invoices?.length > 0) {
      return sourceDocs.invoices;
    }
    // 从 doc.invoiceNos 获取
    if (doc.sourceInvoiceNos?.length > 0) {
      return doc.sourceInvoiceNos.map(invoiceNo => {
        const delivery = deliveries?.find(d => d.invoice?.no === invoiceNo);
        if (delivery?.invoice) {
          return {
            docNo: invoiceNo,
            amount: delivery.invoice.amount,
            type: delivery.invoice.type,
            at: delivery.invoice.at,
          };
        }
        return { docNo: invoiceNo, amount: 0 };
      });
    }
    // 从 doc.delivery 获取单个发票
    if (doc.delivery?.invoice) {
      return [{
        docNo: doc.delivery.invoice.no,
        amount: doc.delivery.invoice.amount,
        type: doc.delivery.invoice.type,
        at: doc.delivery.invoice.at,
      }];
    }
    return [];
  };

  // 获取关联的结算单数据
  const getSettlements = () => {
    // 优先从 sourceDocs 获取
    if (sourceDocs?.settlements?.length > 0) {
      return sourceDocs.settlements;
    }
    // 从 doc.sourceSettlementNos 获取
    if (doc.sourceSettlementNos?.length > 0) {
      return doc.sourceSettlementNos.map(settlementNo => {
        const delivery = deliveries?.find(d => d.settlement?.settlementNo === settlementNo);
        if (delivery?.settlement) {
          return {
            docNo: settlementNo,
            amount: delivery.settlement.amount,
            actualWeight: delivery.settlement.actualWeight || delivery.storage?.actualWeight,
            unitPrice: delivery.settlement.unitPrice,
            confirmedAt: delivery.settlement.confirmedAt,
            confirmedBySupplier: delivery.settlement.confirmedBySupplier,
          };
        }
        return { docNo: settlementNo, amount: 0 };
      });
    }
    // 从 doc.delivery 获取单个结算单
    if (doc.delivery?.settlement) {
      return [{
        docNo: doc.delivery.settlement.settlementNo,
        amount: doc.delivery.settlement.amount,
        actualWeight: doc.delivery.settlement.actualWeight || doc.delivery?.storage?.actualWeight,
        unitPrice: doc.delivery.settlement.unitPrice,
        confirmedAt: doc.delivery.settlement.confirmedAt,
        confirmedBySupplier: doc.delivery.settlement.confirmedBySupplier,
      }];
    }
    return [];
  };

  const invoices = getInvoices();
  const settlements = getSettlements();

  // 处理点击发票单跳转
  const handleInvoiceClick = (invoiceDocNo) => {
    if (!deliveries || !onNavigateToDocument) return;

    const delivery = deliveries.find(d => d.invoice?.no === invoiceDocNo);
    if (!delivery) return;

    const invoiceDoc = {
      id: delivery.invoice.no,
      docNo: delivery.invoice.no,
      docType: 'invoice',
      orderId: delivery.orderId,
      orderNo: delivery.orderNo,
      deliveryId: delivery.id,
      deliveryNo: delivery.deliveryNo,
      supplierId: delivery.supplierId,
      supplierName: delivery.supplierName,
      baseId: delivery.baseId,
      baseName: delivery.baseName,
      materialName: delivery.materialName,
      amount: delivery.invoice.amount,
      invoiceType: delivery.invoice.type,
      at: delivery.invoice.at,
      status: delivery.payment ? '已付款' : '待付款',
      createdAt: delivery.invoice.at,
      delivery: delivery,
      isMerged: (delivery.invoice.settlementNos?.length || 0) >= 2,
      settlementNos: delivery.invoice.settlementNos || null,
    };

    onNavigateToDocument(invoiceDoc);
  };

  // 处理点击结算单跳转
  const handleSettlementClick = (settlementDocNo) => {
    if (!deliveries || !onNavigateToDocument) return;

    const delivery = deliveries.find(d => d.settlement?.settlementNo === settlementDocNo);
    if (!delivery) return;

    const settlementDoc = {
      id: delivery.settlement.settlementNo,
      docNo: delivery.settlement.settlementNo,
      docType: 'settlement',
      orderId: delivery.orderId,
      orderNo: delivery.orderNo,
      deliveryId: delivery.id,
      deliveryNo: delivery.deliveryNo,
      supplierId: delivery.supplierId,
      supplierName: delivery.supplierName,
      baseId: delivery.baseId,
      baseName: delivery.baseName,
      materialName: delivery.materialName,
      actualWeight: delivery.settlement.actualWeight || delivery.storage?.actualWeight,
      unitPrice: delivery.settlement.unitPrice,
      amount: delivery.settlement.amount,
      confirmedBySupplier: delivery.settlement.confirmedBySupplier,
      confirmedAt: delivery.settlement.confirmedAt,
      status: delivery.settlement.confirmedBySupplier ? '已结算' : '待结算',
      invoiceStatus: delivery.invoice ? '已开票' : '未开票',
      createdAt: delivery.storage?.at || delivery.createdAt,
      delivery: delivery,
      isMerged: (delivery.settlement.sourceStockInNos?.length || 0) >= 2,
      sourceStockInNos: delivery.settlement.sourceStockInNos || null,
    };

    onNavigateToDocument(settlementDoc);
  };

  return (
    <div className="space-y-6">
      <InfoCard title="付款信息" icon={CreditCard}>
        {isMerged && (
          <div className="mb-3 px-3 py-2 bg-blue-50 rounded-lg text-xs text-blue-700 flex items-center gap-2">
            <Link2 size={14} />
            <span>合并付款单 - 由 {sourceDocs?.invoices?.length || 0} 个发票单合并生成</span>
          </div>
        )}
        <div className="p-4 bg-green-50 rounded-lg mb-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-green-600">付款金额</p>
              <p className="text-2xl font-bold text-green-700">¥{doc.amount?.toLocaleString()}</p>
            </div>
            <div className="text-right">
              <p className="text-xs text-green-600">付款状态</p>
              <p className={`text-lg font-bold ${
                doc.status === '已付款' ? 'text-green-600' :
                doc.status === '待付款' ? 'text-amber-600' : 'text-blue-600'
              }`}>
                {doc.status}
              </p>
            </div>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <InfoRow label="付款单号" value={doc.docNo} />
          <InfoRow label="付款时间" value={doc.paymentTime || '待付款'} />
          <InfoRow label="付款方式" value={doc.paymentMethod || '待确定'} />
          <InfoRow label="交易流水号" value={doc.transactionNo || '待生成'} />
        </div>
      </InfoCard>

      {/* 合并单据 - 多个发票单 */}
      {isMerged && invoices.length > 0 && (
        <InfoCard title={`关联发票单 (${invoices.length})`} icon={Receipt}>
          <div className="space-y-3">
            {invoices.map((invoice, index) => (
              <ClickableDocCard
                key={invoice.docNo || index}
                docNo={invoice.docNo}
                onClick={() => handleInvoiceClick(invoice.docNo)}
                color="teal"
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs text-slate-500">发票金额</span>
                  <span className="text-sm font-bold text-teal-600">¥{(invoice.amount || 0).toLocaleString()}</span>
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs text-slate-500">
                  <span>类型: {invoice.type || '-'}</span>
                  <span>日期: {invoice.at || '-'}</span>
                </div>
              </ClickableDocCard>
            ))}
            <div className="pt-2 border-t border-slate-200 flex justify-between text-sm">
              <span className="text-slate-500">合计金额</span>
              <span className="font-bold text-teal-600">
                ¥{invoices.reduce((sum, i) => sum + (i.amount || 0), 0).toLocaleString()}
              </span>
            </div>
          </div>
        </InfoCard>
      )}

      {/* 合并单据 - 多个结算单 */}
      {isMerged && settlements.length > 0 && (
        <InfoCard title={`关联结算单 (${settlements.length})`} icon={DollarSign}>
          <div className="space-y-3">
            {settlements.map((settlement, index) => (
              <ClickableDocCard
                key={settlement.docNo || index}
                docNo={settlement.docNo}
                onClick={() => handleSettlementClick(settlement.docNo)}
                color="orange"
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs text-slate-500">结算金额</span>
                  <span className="text-sm font-bold text-orange-600">¥{(settlement.amount || 0).toLocaleString()}</span>
                </div>
                <div className="grid grid-cols-3 gap-2 text-xs text-slate-500">
                  <span>实重: {settlement.actualWeight || '-'} 吨</span>
                  <span>单价: ¥{settlement.unitPrice || '-'}/吨</span>
                  <span>确认: {settlement.confirmedAt || (settlement.confirmedBySupplier ? '已确认' : '待确认')}</span>
                </div>
              </ClickableDocCard>
            ))}
            <div className="pt-2 border-t border-slate-200 flex justify-between text-sm">
              <span className="text-slate-500">合计金额</span>
              <span className="font-bold text-orange-600">
                ¥{settlements.reduce((sum, s) => sum + (s.amount || 0), 0).toLocaleString()}
              </span>
            </div>
          </div>
        </InfoCard>
      )}

      {/* 单个结算单 - 非合并情况 */}
      {!isMerged && settlements.length === 1 && (
        <InfoCard title="关联结算单" icon={DollarSign}>
          <ClickableDocCard
            docNo={settlements[0].docNo}
            onClick={() => handleSettlementClick(settlements[0].docNo)}
            color="orange"
          >
            <div className="space-y-2">
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div><span className="text-slate-500">实重:</span> <span className="text-slate-700">{settlements[0].actualWeight || '-'} 吨</span></div>
                <div><span className="text-slate-500">单价:</span> <span className="text-slate-700">¥{settlements[0].unitPrice || '-'}/吨</span></div>
                <div><span className="text-slate-500">供应商确认:</span> {settlements[0].confirmedBySupplier ? (
                  <span className="text-green-600">已确认</span>
                ) : (
                  <span className="text-orange-600">待确认</span>
                )}</div>
                <div><span className="text-slate-500">确认时间:</span> <span className="text-slate-700">{settlements[0].confirmedAt || '待确认'}</span></div>
              </div>
              <div className="pt-2 border-t border-slate-200 flex justify-between">
                <span className="text-xs text-slate-500">结算金额</span>
                <span className="text-sm font-bold text-orange-600">¥{(settlements[0].amount || 0).toLocaleString()}</span>
              </div>
            </div>
          </ClickableDocCard>
        </InfoCard>
      )}

      {/* 单个发票单 - 非合并情况 */}
      {!isMerged && invoices.length === 1 && (
        <InfoCard title="关联发票单" icon={Receipt}>
          <ClickableDocCard
            docNo={invoices[0].docNo}
            onClick={() => handleInvoiceClick(invoices[0].docNo)}
            color="teal"
          >
            <div className="space-y-2">
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div><span className="text-slate-500">发票类型:</span> <span className="text-slate-700">{invoices[0].type || '-'}</span></div>
                <div><span className="text-slate-500">开票日期:</span> <span className="text-slate-700">{invoices[0].at || '-'}</span></div>
              </div>
              <div className="pt-2 border-t border-slate-200 flex justify-between">
                <span className="text-xs text-slate-500">发票金额</span>
                <span className="text-sm font-bold text-teal-600">¥{(invoices[0].amount || 0).toLocaleString()}</span>
              </div>
            </div>
          </ClickableDocCard>
        </InfoCard>
      )}

      <InfoCard title="关联订单" icon={FileText}>
        <div className="grid grid-cols-2 gap-4">
          <InfoRow label="订单编号" value={order?.orderNo} />
          <InfoRow label="供应商" value={order?.supplierName} />
          <InfoRow label="物料" value={order?.materialName} />
          <InfoRow label="基地" value={order?.baseName} />
        </div>
      </InfoCard>
    </div>
  );
};

// 主组件
export default function DocumentDetailView({ document, order, deliveries = [], stockIns = [], onBack, onviewDelivery, onNavigateToDocument }) {
  if (!document) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-slate-500">单据不存在</p>
      </div>
    );
  }

  const config = DOC_TYPE_CONFIG[document.docType];
  const Icon = config.icon;

  // 查找关联的源单据
  const findSourceDocuments = () => {
    const sourceDocs = {
      stockins: [],
      settlements: [],
      invoices: [],
    };

    // 对于结算单，查找关联的入库单（支持 sourceStockInNos 和 stockInNos 两种字段名）
    // 不再要求必须是合并单据，只要有入库单号就查找
    const stockInNoList = document.sourceStockInNos || document.stockInNos;
    if (document.docType === 'settlement' && stockInNoList?.length > 0) {
      // 先从 deliveries 中查找
      const foundStockInNos = new Set();
      deliveries.forEach(del => {
        if (del.storage && stockInNoList.includes(del.storage.stockInNo)) {
          foundStockInNos.add(del.storage.stockInNo);
          sourceDocs.stockins.push({
            docNo: del.storage.stockInNo,
            actualWeight: del.storage.actualWeight,
            at: del.storage.at,
            vehicleNo: del.reservation?.vehicleNo,
            operator: del.storage.operator,
            quality: del.storage.quality,
            grossWeight: del.storage.grossWeight,
            tareWeight: del.storage.tareWeight,
            netWeight: del.storage.netWeight,
            deduction: del.storage.deduction,
          });
        }
      });

      // 对于 deliveries 中找不到的入库单，从 stockIns 数组中查找
      stockInNoList.forEach(stockInNo => {
        if (!foundStockInNos.has(stockInNo)) {
          const stockIn = stockIns.find(s => s.id === stockInNo);
          if (stockIn) {
            sourceDocs.stockins.push({
              docNo: stockIn.id,
              actualWeight: stockIn.actualWeight,
              at: stockIn.inboundTime,
              vehicleNo: stockIn.plate,
              operator: stockIn.operator || '系统',
              quality: stockIn.quality,
              grossWeight: stockIn.grossWeight,
              tareWeight: stockIn.tareWeight,
              netWeight: stockIn.netWeight,
              deduction: stockIn.deduction,
            });
          }
        }
      });
    }

    // 如果不是合并单据，其他类型的源单据不需要查找
    if (!document.isMerged) {
      // 对于结算单，即使不是合并单据，也返回入库单
      if (document.docType === 'settlement' && sourceDocs.stockins.length > 0) {
        return sourceDocs;
      }
      // 对于付款单，即使不是合并单据，也需要查找关联的发票和结算单
      if (document.docType === 'payment') {
        // 继续执行下面的付款单处理逻辑
      } else if (document.docType === 'invoice') {
        // 对于发票单，即使不是合并单据，也需要查找关联的结算单
      } else {
        return null;
      }
    }

    // 对于发票单，查找关联的结算单
    if (document.docType === 'invoice') {
      // 支持 sourceSettlementNos 和 settlementNos 两种字段名
      const settlementNos = document.sourceSettlementNos || document.settlementNos;
      if (settlementNos) {
        // 使用 Set 去重，避免同一个结算单被添加多次
        const addedSettlementNos = new Set();
        deliveries.forEach(del => {
          if (del.settlement && settlementNos.includes(del.settlement.settlementNo) && !addedSettlementNos.has(del.settlement.settlementNo)) {
            addedSettlementNos.add(del.settlement.settlementNo);
            sourceDocs.settlements.push({
              docNo: del.settlement.settlementNo,
              amount: del.settlement.amount,
              actualWeight: del.settlement.actualWeight || del.storage?.actualWeight,
              unitPrice: del.settlement.unitPrice,
              confirmedAt: del.settlement.confirmedAt,
            });
          }
        });
      }
    }

    // 对于付款单，查找关联的结算单和发票单
    if (document.docType === 'payment') {
      // 使用 Set 去重
      const addedSettlementNos = new Set();
      const addedInvoiceNos = new Set();

      if (document.sourceSettlementNos) {
        deliveries.forEach(del => {
          if (del.settlement && document.sourceSettlementNos.includes(del.settlement.settlementNo) && !addedSettlementNos.has(del.settlement.settlementNo)) {
            addedSettlementNos.add(del.settlement.settlementNo);
            sourceDocs.settlements.push({
              docNo: del.settlement.settlementNo,
              amount: del.settlement.amount,
              actualWeight: del.settlement.actualWeight || del.storage?.actualWeight,
              unitPrice: del.settlement.unitPrice,
              confirmedAt: del.settlement.confirmedAt,
            });
          }
        });
      }
      if (document.sourceInvoiceNos) {
        deliveries.forEach(del => {
          if (del.invoice && document.sourceInvoiceNos.includes(del.invoice.no) && !addedInvoiceNos.has(del.invoice.no)) {
            addedInvoiceNos.add(del.invoice.no);
            sourceDocs.invoices.push({
              docNo: del.invoice.no,
              amount: del.invoice.amount,
              type: del.invoice.type,
              at: del.invoice.at,
            });
          }
        });
      }
    }

    return sourceDocs;
  };

  const sourceDocs = findSourceDocuments();

  // 根据单据类型渲染不同内容
  const renderDetail = () => {
    switch (document.docType) {
      case 'arrival_notice':
        return <ArrivalNoticeDetail doc={document} order={order} />;
      case 'receipt':
        return <ReceiptDetail doc={document} order={order} onNavigateToDocument={onNavigateToDocument} />;
      case 'stockin':
        return <StockinDetail doc={document} order={order} onNavigateToDocument={onNavigateToDocument} />;
      case 'settlement':
        return <SettlementDetail doc={document} sourceDocs={sourceDocs} deliveries={deliveries} stockIns={stockIns} onNavigateToDocument={onNavigateToDocument} />;
      case 'invoice':
        return <InvoiceDetail doc={document} sourceDocs={sourceDocs} deliveries={deliveries} onNavigateToDocument={onNavigateToDocument} />;
      case 'payment':
        return <PaymentDetail doc={document} order={order} sourceDocs={sourceDocs} deliveries={deliveries} onNavigateToDocument={onNavigateToDocument} />;
      default:
        return <p className="text-slate-500">未知单据类型</p>;
    }
  };

  // 判断是否显示"查看完整送货单"按钮（结算单和发票单不显示）
  const showDeliveryButton = !['settlement', 'invoice'].includes(document.docType);

  return (
    <div className="space-y-6">
      {/* 返回按钮和标题 */}
      <div className="flex items-center gap-4">
        <button
          onClick={onBack}
          className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
        >
          <ChevronLeft size={20} className="text-slate-600" />
        </button>
        <div className="flex-1">
          <h2 className="text-xl font-bold text-slate-800">{config.label}</h2>
          <p className="text-sm text-slate-500">{config.noLabel}: {document.docNo}</p>
        </div>
        <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-${config.color}-50`}>
          <Icon size={18} className={`text-${config.color}-600`} />
          <span className={`font-medium text-${config.color}-700`}>{config.label}</span>
        </div>
      </div>

      {/* 主要内容 */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* 左侧 - 单据详情（结算单和发票单使用全宽） */}
        <div className={['settlement', 'invoice'].includes(document.docType) ? 'lg:col-span-3' : 'lg:col-span-2'}>
          {renderDetail()}
        </div>

        {/* 右侧 - 快捷操作（结算单和发票单不显示） */}
        {!['settlement', 'invoice'].includes(document.docType) && (
        <div className="space-y-6">
          {/* 操作按钮 */}
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <h3 className="font-medium text-slate-700 mb-4">快捷操作</h3>
            <div className="space-y-3">
              {showDeliveryButton && (
                <button
                  onClick={() => onviewDelivery?.(document.deliveryId)}
                  className="w-full flex items-center justify-center gap-2 py-2.5 bg-blue-500 text-white rounded-lg font-medium hover:bg-blue-600 transition-colors"
                >
                  <Eye size={16} />
                  查看完整送货单
                </button>
              )}
              {!showDeliveryButton && (
                <p className="text-sm text-slate-400 text-center py-2">
                  点击关联单据可查看详情
                </p>
              )}
            </div>
          </div>

          {/* 单据摘要 */}
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <h3 className="font-medium text-slate-700 mb-4">单据摘要</h3>
            <div className="space-y-2 text-sm">
              {document.isMerged && (
                <div className="flex justify-between items-center text-blue-600">
                  <span>单据类型</span>
                  <span className="font-medium flex items-center gap-1">
                    <Link2 size={12} />
                    合并单据
                  </span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-slate-500">送货单号</span>
                <span className="font-medium text-slate-800">{document.deliveryNo}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">订单号</span>
                <span className="font-medium text-slate-800">{document.orderNo}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">供应商</span>
                <span className="font-medium text-slate-800">{document.supplierName}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">基地</span>
                <span className="font-medium text-slate-800">{document.baseName}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">创建时间</span>
                <span className="font-medium text-slate-800">{document.createdAt}</span>
              </div>
            </div>
          </div>
        </div>
        )}
      </div>
    </div>
  );
}
